import { ReactIntegration, createReactRouterV6Options } from '@grafana/faro-react';
import {
  ErrorsInstrumentation,
  SessionInstrumentation,
  WebVitalsInstrumentation,
  initializeFaro,
} from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import type { Span } from '@opentelemetry/api';
import { W3CTraceContextPropagator } from '@opentelemetry/core';
import { Routes, createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';
import { sanitizeFaroItem } from './sanitizeFaroItem';

interface BrowserFaroOptions {
  endpoint: string | undefined;
  sampleRate: string | undefined;
  version: string;
  environment: string;
}

export function initializeBrowserFaro(options: BrowserFaroOptions) {
  if (!options.endpoint) {
    return undefined;
  }
  const samplingRate = options.sampleRate === undefined ? 1 : Number(options.sampleRate);
  if (
    options.endpoint !== '/faro/collect' ||
    options.sampleRate?.trim() === '' ||
    !Number.isFinite(samplingRate) ||
    samplingRate < 0 ||
    samplingRate > 1
  ) {
    console.warn('Browser telemetry is disabled: configure /faro/collect and a session sample rate between 0 and 1.');
    return undefined;
  }
  const origin = window.location.origin;
  const escapedOrigin = origin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const redactRequest = (span: Span) => {
    span.setAttribute('url.full', `${origin}/api`);
    span.setAttribute('http.url', `${origin}/api`);
  };
  return initializeFaro({
    url: `${origin}${options.endpoint}`,
    app: { name: 'AutoScript Frontend', version: options.version, environment: options.environment },
    metas: [],
    preventGlobalExposure: true,
    sessionTracking: { enabled: true, persistent: false, samplingRate },
    experimental: { trackNavigation: false },
    batching: { sendTimeout: 1000 },
    ignoreUrls: [new RegExp(`^(?!(?:${escapedOrigin})?/api(?:/|$))`)],
    beforeSend: sanitizeFaroItem,
    instrumentations: [
      new ErrorsInstrumentation(),
      new WebVitalsInstrumentation(),
      new SessionInstrumentation(),
      new TracingInstrumentation({
        propagator: new W3CTraceContextPropagator(),
        instrumentationOptions: {
          propagateTraceHeaderCorsUrls: [],
          fetchInstrumentationOptions: { ignoreNetworkEvents: true, applyCustomAttributesOnSpan: redactRequest },
          xhrInstrumentationOptions: { ignoreNetworkEvents: true, applyCustomAttributesOnSpan: redactRequest },
        },
      }),
      new ReactIntegration({
        router: createReactRouterV6Options({
          Routes,
          createRoutesFromChildren,
          matchRoutes,
          useLocation,
          useNavigationType,
        }),
      }),
    ],
  });
}
